import {useMemo, useRef} from 'react'
import {RenderState, type PreviewData} from './types'
import {useIFrameMessaging} from './use-iframe-messaging'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'

import sandboxStyles from './SandboxView.module.css'
import {ErrorMessage} from './ErrorMessage'

function sandboxUrl(viewscreenUrl: string, restrictPermissions: boolean) {
  const format = restrictPermissions ? 'embedded_html' : 'sandbox'
  return new URL(`view/${format}?url=client://sandbox`, viewscreenUrl).toString()
}

const RESTRICTED_PERMISSIONS_POLICY = [
  "accelerometer 'none'",
  "autoplay 'none'",
  "camera 'none'",
  "clipboard-read 'none'",
  "clipboard-write 'none'",
  "display-capture 'none'",
  "encrypted-media 'none'",
  "fullscreen 'none'",
  "geolocation 'none'",
  "gyroscope 'none'",
  "magnetometer 'none'",
  "microphone 'none'",
  "midi 'none'",
  "payment 'none'",
  "picture-in-picture 'none'",
  "publickey-credentials-get 'none'",
  "screen-wake-lock 'none'",
  "usb 'none'",
  "web-share 'none'",
  "xr-spatial-tracking 'none'",
].join('; ')

export interface SandboxProps extends PreviewData {
  viewscreenUrl: string
  title?: string
  restrictPermissions?: boolean
}

export function SandboxView({
  content,
  inlineScripts,
  inlineStyles,
  headScripts,
  bodyScripts,
  styles,
  viewscreenUrl,
  title = 'Preview page for the user provided code',
  restrictPermissions = false,
}: SandboxProps) {
  const sandboxEnabled = isFeatureEnabled('viewscreen_sandbox')
  const url = sandboxUrl(viewscreenUrl, restrictPermissions)
  const origin = new URL(url, ssrSafeLocation.origin).origin

  const iFrameRef = useRef<HTMLIFrameElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const previewData = useMemo<PreviewData>(
    () => ({
      content,
      headScripts,
      bodyScripts,
      inlineScripts,
      inlineStyles,
      styles,
    }),
    [bodyScripts, content, headScripts, inlineScripts, inlineStyles, styles],
  )

  const {renderState, errorMessage} = useIFrameMessaging(
    iFrameRef,
    containerRef,
    previewData,
    origin,
    restrictPermissions,
  )

  if (!sandboxEnabled) return null

  sendEvent('sandbox_view.activate', {target: 'SANDBOX_INITIALIZED', enabled: sandboxEnabled})

  // Collect the URL origins for headScripts, bodyScript, styles and send them as events to telemetry
  const origins = Array.from(
    new Set(
      [...(headScripts || []), ...(bodyScripts || []), ...(styles || [])]
        .map(script => {
          try {
            return new URL(script, origin).origin
          } catch {
            return null
          }
        })
        .filter(Boolean),
    ),
  )

  for (const resourceOrigin of origins) {
    sendEvent('sandbox_view.activate', {target: 'SANDBOX_RESOURCE_ORIGIN', origin: resourceOrigin})
  }

  return (
    <div ref={containerRef} className={sandboxStyles.container}>
      {renderState === RenderState.ERROR ? (
        <ErrorMessage error={errorMessage} />
      ) : (
        <iframe
          ref={iFrameRef}
          className={sandboxStyles.iframe}
          // eslint-disable-next-line @eslint-react/dom-no-missing-iframe-sandbox
          sandbox={restrictPermissions ? 'allow-scripts' : 'allow-scripts allow-same-origin'}
          allow={restrictPermissions ? RESTRICTED_PERMISSIONS_POLICY : undefined}
          referrerPolicy="no-referrer"
          title={title}
          src={url}
        />
      )}
    </div>
  )
}
